import { computed } from 'vue';
import { get } from 'lodash';
import { storeToRefs } from 'pinia';
import { useAssortmentCanvasStore } from '@/stores/assortment-canvas';
import { useContextStore } from '@/stores/context';
import { useScenarioCdtsStore } from '@/stores/scenario-cdts';

/**
 * Provides reactive products aggregated across all CDT nodes for a given cann group.
 * Intended for use in the collapsed cann group view on the assortment canvas.
 *
 * @param {import('vue').Ref<string>} cannGroupId - Reactive ref containing the cann group ID.
 * @returns {{ allCannGroupProducts: import('vue').ComputedRef, childTree: import('vue').ComputedRef }}
 */
export function useCannGroupProducts(cannGroupId) {
  const assortmentCanvasStore = useAssortmentCanvasStore();
  const scenarioCdtsStore = useScenarioCdtsStore();
  const contextStore = useContextStore();

  const {
    productsByCdtGroup,
    getFilteredProducts,
    getHighlightedProducts,
    sortByProductsInCanvas,
  } = storeToRefs(assortmentCanvasStore);
  const { childrenLookup } = storeToRefs(scenarioCdtsStore);
  const { getClientConfig } = storeToRefs(contextStore);

  const childTree = computed(() => childrenLookup.value(cannGroupId.value));

  const hasHighlightProductEnabled = computed(() =>
    get(getClientConfig.value, 'features.highlightProductEnabled', false)
  );

  const allCannGroupProducts = computed(() => {
    const allNodeIds = Object.keys(childTree.value);
    const productsInView = hasHighlightProductEnabled.value
      ? getHighlightedProducts.value
      : getFilteredProducts.value;
    return allNodeIds
      .reduce((acc, nodeId) => {
        const products = productsByCdtGroup.value[nodeId] || [];
        const visibleProducts = products.filter(p =>
          productsInView.visibleProductsSet.has(p.productKey)
        );
        visibleProducts.forEach(p => {
          p.isHighlighted = hasHighlightProductEnabled.value
            ? productsInView.highlightedProductsSet.has(p.productKey)
            : false;
        });
        return acc.concat(visibleProducts);
      }, [])
      .sort(sortByProductsInCanvas.value);
  });

  return { allCannGroupProducts, childTree };
}
